import * as React from 'react'
import { styled, css } from 'next-yak'

import * as api from '@owl-nest/api-client/latest'
import * as dateutils from '@owl-nest/date'
import { t, tc, tn } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { MoneyDisplayer } from '@owl-nest/money'
import * as plume from '@ulule/owl-kit-components/next'
import * as Enum from '@owl-nest/legacy-enum'

import * as projectModel from '../../../../models/project'

import { RewardList } from './RewardList'

const OrderStatus = Enum.create({
  VALID: 0,
  CANCELLED: 1,
  NEED_RENEW: 2,
  FAILED: 3,
})
type OrderStatus = Enum.ValueOf<typeof OrderStatus>

type ContributionProps = {
  project: projectModel.Project
  order: api.Order
}

export function Contribution({ project, order }: ContributionProps): React.ReactElement<ContributionProps> {
  if (model.project.isDonationBased(project)) {
    return <Donation project={project} order={order} />
  }

  const status = orderStatus(order)
  const deliveryType = order.shipping_type

  const renewCopy =
    order.payment_method === api.PaymentMethod.PAYPAL
      ? t('Confirm your Paypal payment')
      : t('Confirm your payment details')

  const skuList = order.items
    ? order.items
        .map((orderItem) => model.sku.Sku<projectModel.ExtraFields>(orderItem, project))
        .filter((sku): sku is model.sku.Sku<projectModel.ExtraFields> => sku !== undefined)
    : []
  const isMonoReward = skuList.length === 1 && order.tip === 0
  const isTipOnly = skuList.length === 0 && order.tip !== 0
  const areSomeRewardLegacy = skuList.some((sku) => {
    if (sku.type === 'reward') {
      return model.reward.isLegacy(sku.item)
    }
    return model.reward.isLegacy(sku.item.parent)
  })

  const displayDetail = !isMonoReward && !areSomeRewardLegacy && !isTipOnly

  return (
    <>
      <ContributionBlock>
        <RewardInformations href={!displayDetail ? order.edit_url : undefined}>
          <RewardBlock>
            <RewardPrice $status={status}>
              <MoneyDisplayer amount={order.order_total} project={project} />
            </RewardPrice>

            <RewardList
              title={title(order, skuList)}
              subtitle={subtitle(skuList)}
              skuList={skuList}
              displayDetail={displayDetail}
            />
          </RewardBlock>

          <plume.styles.copy.XS.regular>
            {deliveryType === api.OrderShippingType.USER_ADDRESS &&
              tc('Home delivery [delivery]', {
                delivery: <strong dangerouslySetInnerHTML={{ __html: formattedDeliveryDate(skuList) }} />,
              })}
            {deliveryType === api.OrderShippingType.PICKUP_POINT &&
              tc('Pickup point [delivery]', {
                delivery: <strong dangerouslySetInnerHTML={{ __html: formattedDeliveryDate(skuList) }} />,
              })}
            {deliveryType === api.OrderShippingType.MONDIAL_RELAY &&
              tc('Mondial Relay delivery [delivery]', {
                delivery: <strong dangerouslySetInnerHTML={{ __html: formattedDeliveryDate(skuList) }} />,
              })}
          </plume.styles.copy.XS.regular>
          {/* TODO: get info from api to know if payment was canceled or never happened */}
          {/*footerNotes && <S.copy.XS>{footerNotes}</S.copy.XS>*/}
        </RewardInformations>

        {order.edit_url && (
          <LinkToContribution href={order.edit_url} kind="inverted">
            <plume.styles.copy.XS.regular>{t('See details')}</plume.styles.copy.XS.regular>
            <plume.glyphs.stroke.ArrowRight size={12} />
          </LinkToContribution>
        )}
      </ContributionBlock>

      {status === OrderStatus.NEED_RENEW && order.renew_url && (
        <plume.Callout type="alert">
          <plume.Link href={order.renew_url}>{renewCopy}</plume.Link>
        </plume.Callout>
      )}
    </>
  )
}

function Donation({ project, order }: ContributionProps) {
  const status = orderStatus(order)
  const isFinishedAndRecurrent = model.project.isFinished(project) && order.subscription !== undefined

  const renewCopy =
    order.payment_method === api.PaymentMethod.PAYPAL
      ? t('Confirm your Paypal payment')
      : t('Confirm your payment details')

  return (
    <>
      <ContributionBlock>
        <RewardInformations>
          <RewardBlock>
            <RewardPrice $status={status} isFinishedAndRecurrent={isFinishedAndRecurrent}>
              <MoneyDisplayer amount={order.order_total} project={project} />
            </RewardPrice>{' '}
            {order.subscription && <Frequency>{t('per month')}</Frequency>}
          </RewardBlock>

          <plume.styles.copy.XS.regular>
            {status === OrderStatus.CANCELLED &&
              !isFinishedAndRecurrent &&
              t('Last payment made on %(date)s', {
                date: dateutils.format(new Date(order.created_at), 'dd/MM/yyyy'),
              })}
            {status === OrderStatus.VALID &&
              !isFinishedAndRecurrent &&
              order.subscription?.next_payment_expected_at &&
              t('Next payment on %(date)s', {
                date: dateutils.format(
                  // HACK: next_payment_expected_at has no time information. For
                  // a canadian user (GMT -5), parsing this date will give a
                  // Date for the day before. Adding time info fixes the issue.
                  new Date(`${order.subscription.next_payment_expected_at}T00:00:00`),
                  'dd/MM/yyyy',
                ),
              })}
            {status === OrderStatus.VALID &&
              !isFinishedAndRecurrent &&
              order.subscription === undefined &&
              t('Payment made on %(date)s', {
                date: dateutils.format(new Date(order.created_at), 'dd/MM/yyyy'),
              })}
            {isFinishedAndRecurrent && t('No upcoming payments')}
          </plume.styles.copy.XS.regular>
        </RewardInformations>

        {order.edit_url && (
          <LinkToContribution href={order.edit_url} kind="inverted">
            <plume.styles.copy.XS.regular>{t('See details')}</plume.styles.copy.XS.regular>
            <plume.glyphs.stroke.ArrowRight size={12} />
          </LinkToContribution>
        )}
      </ContributionBlock>

      {(status === OrderStatus.FAILED || status === OrderStatus.NEED_RENEW) && order.renew_url && (
        <plume.Callout type="alert">
          <plume.Link href={order.renew_url}>{renewCopy}</plume.Link>
        </plume.Callout>
      )}
    </>
  )
}

function orderStatus(order: api.Order): OrderStatus {
  if (
    order.status === 'cancelled' ||
    order.status === 'payment-reimbursed' ||
    order?.subscription?.status === api.SubscriptionStatus.CANCELED
  ) {
    return OrderStatus.CANCELLED
  } else if (order.status === 'payment-invalid') {
    return OrderStatus.NEED_RENEW
  } else if (order.subscription?.status === api.SubscriptionStatus.FAILED) {
    return OrderStatus.FAILED
  }
  return OrderStatus.VALID
}

function title(order: api.Order, skuList: model.sku.Sku<projectModel.ExtraFields>[]): string {
  const quantity = skuList.map((item) => item.quantity).reduce((a, b) => a + b, 0)

  if (order.tip > 0) {
    let title = t('Free donation')
    if (quantity > 0) {
      title = `${title} + ${tn('%d reward', '%d rewards', quantity)}`
    }
    return title
  }

  const firstSkuItem = skuList[0]

  // HACK: For old free donation contributions with no tip and no reward
  // Can be deleted when all old orders are migrated
  if (!firstSkuItem && order.tip === 0) return t('Free donation')

  if (skuList.length > 1 || (firstSkuItem && model.sku.isLegacy(firstSkuItem))) {
    return tn('%d reward', '%d rewards', quantity)
  }

  const title = model.sku.title(firstSkuItem)
  if (firstSkuItem.quantity > 1 && title !== undefined) {
    return t('%(quantity)dx %(title)s', { quantity, title })
  }

  return title || ''
}

function subtitle(skuList: model.sku.Sku<projectModel.ExtraFields>[]): string | undefined {
  if (skuList.length === 1 && skuList[0].type === 'variant') {
    return model.variant.description(skuList[0].item)
  }
}

function deduplicateAndSortByDeliveryDate(
  rewards: model.reward.Reward<projectModel.ExtraFields>[],
): model.reward.Reward<projectModel.ExtraFields>[] {
  const seen: { [s: string]: boolean } = {}
  const deduplicated: model.reward.Reward<projectModel.ExtraFields>[] = []

  for (const reward of rewards) {
    const date = reward.shippings?.date_delivery || ''

    if (!(date in seen)) {
      seen[date] = true
      deduplicated.push(reward)
    }
  }

  deduplicated.sort((a, b) => {
    const dateA = a.shippings?.date_delivery
    const dateB = b.shippings?.date_delivery
    if (!dateA && !dateB) {
      return 0
    }
    if (!dateA) {
      return 1
    }
    if (!dateB) {
      return -1
    }
    return dateutils.compareAsc(new Date(dateA), new Date(dateB))
  })

  return deduplicated
}

function formattedDeliveryDate(skuList: model.sku.Sku<projectModel.ExtraFields>[]): string {
  const rewardsDeduplicatedAndSortedByDeliveryDate = deduplicateAndSortByDeliveryDate(
    skuList.map((sku) => model.sku.getReward(sku)),
  )

  if (rewardsDeduplicatedAndSortedByDeliveryDate.length === 1) {
    return model.reward.formattedDeliveryDate(rewardsDeduplicatedAndSortedByDeliveryDate[0], 'MMMM yyyy')
  } else if (rewardsDeduplicatedAndSortedByDeliveryDate.length > 1) {
    return t('As of %(date)s', {
      date: model.reward.formattedDeliveryDate(rewardsDeduplicatedAndSortedByDeliveryDate[0], 'MMMM yyyy'),
    })
  }

  return ''
}

const Frequency = styled(plume.styles.heading.XXXS)`
  margin-left: 8px;
  color: ${plume.COLORS.TEXT_PRIMARY};
`

const ContributionBlock = styled.div`
  align-items: stretch;
  display: flex;
  position: relative;

  ${plume.styles.copy.XS.regular} {
    color: ${plume.COLORS.TEXT_PRIMARY};
  }

  &:nth-child(2) {
    margin-top: 10px;
  }

  & + & {
    margin-top: 4px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    &:nth-child(2) {
      margin-top: 4px;
    }
  }
`

const RewardInformations = styled.a`
  background-color: ${plume.COLORS.BACKGROUND_SUCCESS};
  box-sizing: border-box;
  flex-grow: 1;
  padding: 16px 8px 8px;
  text-decoration: none;
  transition: 0.15s ease;

  ${plume.styles.copy.XS.regular} {
    text-align: left;
    color: ${plume.COLORS.TEXT_SECONDARY};

    strong {
      text-transform: uppercase;
      font-weight: bold;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding: 5px 0 8px 8px;
  }
`

const RewardBlock = styled.div`
  align-items: baseline;
  display: flex;
  margin-bottom: 8px;
`

const RewardPrice = styled(plume.styles.heading.L)<{ $status: OrderStatus; isFinishedAndRecurrent?: boolean }>`
  flex: none;
  position: relative;

  color: ${(props) => {
    if (props.isFinishedAndRecurrent) {
      return plume.COLORS.TEXT_PRIMARY
    }
    if (props.$status === OrderStatus.VALID) {
      return plume.COLORS.SECONDARY_GREEN_800
    }
    if (props.$status === OrderStatus.CANCELLED) {
      return plume.COLORS.TEXT_TERTIARY
    }
    if (props.$status === OrderStatus.NEED_RENEW || props.$status === OrderStatus.FAILED) {
      return plume.COLORS.SECONDARY_YELLOW_800
    }
  }};

  ${(props) => {
    if (props.$status === OrderStatus.CANCELLED) {
      return css`
        &::after {
          background-color: ${plume.COLORS.TEXT_SECONDARY};
          border-radius: 3px;
          bottom: 10px;
          content: '';
          height: 3px;
          left: -5%;
          position: absolute;
          transform: rotateZ(-16deg);
          width: 110%;
          z-index: 10;
        }
      `
    }
  }}
`

const LinkToContribution = styled(plume.Link)`
  align-items: end;
  bottom: 0px;
  display: flex;
  flex: none;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 0px;

  ${plume.styles.copy.XS.regular} {
    margin-right: 5px;
  }

  ${plume.glyphs.Icon} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
    position: relative;
    top: -3px;
  }
`
